import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { VenueCard } from '../components/Venues/VenueCard';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { VenueFilters } from '../components/Venues/Filters/VenueFilters';
import { VenueForm } from '../components/Listings/Forms/VenueForm';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import { europeanCountries } from '../lib/constants/countries';
import { getCurrentLocation, filterByDistance, type LocationCoords } from '../lib/utils/locationFilter';
import type { Venue } from '../types/venues';

interface Filters {
  venueTypes: string[];
  amenities: string[];
  capacity: string;
  countries: string[];
  nearMe: boolean;
  forRent: boolean;
}

export function Venues() {
  const PAGE_SIZE = 24;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [filteredVenues, setFilteredVenues] = useState<(Venue & { distance?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userLocation, setUserLocation] = useState<LocationCoords | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    venueTypes: [],
    amenities: [],
    capacity: '',
    countries: [],
    nearMe: false,
    forRent: false
  });
  const userHasInteracted = useRef(false);
  const searchQueryParam = searchParams.get('q')?.trim() || '';
  const locationParamRaw = searchParams.get('location')?.trim() || '';
  const locationKeyword = locationParamRaw.split(',')[0]?.trim() || locationParamRaw;

  // Initialize filters from URL parameters
  useEffect(() => {
    const countryParam = searchParams.get('country');

    if (countryParam) {
      setFilters(prev => ({
        ...prev,
        countries: [countryParam]
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(0);
    setVenues([]);
  }, [filters, searchParams, refreshKey]);

  useEffect(() => {
    async function fetchVenues() {
      try {
        if (page === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        // Only use URL parameter if user hasn't interacted with filters yet
        const countryParam = searchParams.get('country');
        const effectiveFilters = (countryParam && !userHasInteracted.current)
          ? { ...filters, countries: [countryParam] }
          : filters;

        let query = supabase
          .from('venues')
          .select(`
            *,
            user:user_id (
              id,
              username,
              full_name,
              avatar_url,
              user_type,
              subscription_status,
              manual_professional
            )
          `)
          .eq('approval_status', 'approved')
          .not('slug', 'is', null)
          .eq('user.user_type', 'professional');

        if (effectiveFilters.venueTypes.length > 0) {
          query = query.in('venue_type', effectiveFilters.venueTypes);
        }

        if (effectiveFilters.countries.length > 0) {
          const expandedCountries = Array.from(new Set(
            effectiveFilters.countries.flatMap((c) => {
              const match = europeanCountries.find(ec => ec.value === c || ec.label === c);
              return match ? [match.value, match.label] : [c];
            })
          ));
          query = query.in('country', expandedCountries);
        }
        if (locationKeyword) {
          query = query.or(`address.ilike.%${locationKeyword}%,country.ilike.%${locationKeyword}%`);
        }

        if (effectiveFilters.amenities.length > 0) {
          query = query.contains('amenities', effectiveFilters.amenities);
        }

        if (effectiveFilters.capacity) {
          switch (effectiveFilters.capacity) {
            case 'under_20':
              query = query.lte('capacity', 19);
              break;
            case '20_50':
              query = query.gte('capacity', 20).lte('capacity', 50);
              break;
            case '50_100':
              query = query.gte('capacity', 50).lte('capacity', 100);
              break;
            case 'over_100':
              query = query.gte('capacity', 100);
              break;
          }
        }

        if (effectiveFilters.forRent) {
          query = query.eq('available_for_rent', true);
        }

        query = query
          .order('created_at', { ascending: false })
          .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

        const { data, error } = await query;
        if (error) throw error;

        const rows = data || [];
        setHasMore(rows.length === PAGE_SIZE);
        setVenues(prev => page === 0 ? rows : [...prev, ...rows]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        if (page === 0) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }

    fetchVenues();
  }, [filters, searchParams, refreshKey, page]);

  // Apply URL and Near Me filters client-side
  useEffect(() => {
    let filtered = [...venues];

    if (searchQueryParam) {
      const query = searchQueryParam.toLowerCase();
      filtered = filtered.filter(venue =>
        venue.name?.toLowerCase().includes(query) ||
        venue.description?.toLowerCase().includes(query) ||
        venue.venue_type?.toLowerCase().includes(query) ||
        venue.address?.toLowerCase().includes(query) ||
        venue.country?.toLowerCase().includes(query)
      );
    }

    if (locationKeyword) {
      const keyword = locationKeyword.toLowerCase();
      filtered = filtered.filter(venue =>
        venue.address?.toLowerCase().includes(keyword) ||
        venue.country?.toLowerCase().includes(keyword)
      );
    }

    if (filters.nearMe && userLocation) {
      filtered = filterByDistance(filtered, userLocation, 100);
    }

    setFilteredVenues(filtered);
  }, [venues, filters.nearMe, userLocation, searchQueryParam, locationKeyword]);

  const handleFilterChange = async (filterType: keyof Filters, values: string[] | boolean) => {
    // Mark that user has interacted with filters
    userHasInteracted.current = true;

    // Handle Near Me filter specially
    if (filterType === 'nearMe' && values === true && !userLocation) {
      setLocationLoading(true);
      try {
        const location = await getCurrentLocation();
        setUserLocation(location);
        setFilters(prev => ({ ...prev, nearMe: true }));
      } catch (err: any) {
        setError(err.message);
        setFilters(prev => ({ ...prev, nearMe: false }));
      } finally {
        setLocationLoading(false);
      }
      return;
    }

    setFilters(prev => ({
      ...prev,
      [filterType]: filterType === 'capacity'
        ? (Array.isArray(values) ? values[0] || '' : '')
        : values
    }));
  };

  const handleAddressSelect = (latitude: number, longitude: number, _address: string) => {
    setUserLocation({ latitude, longitude });
    setFilters(prev => ({ ...prev, nearMe: true }));
  };

  const handleCreateListing = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else {
      setShowCreateModal(true);
    }
  };

  const displayVenues = filteredVenues;

  return (
    <div>
      <Meta
        title={`${t('venues.title')} | Mindful Family`}
        description={t('venues.subtitle')}
      />

      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-nature-venue.png"
          alt="Explore Wellness Venues"
          className="w-full h-full object-cover object-bottom shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('venues.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('venues.subtitle')}
        </p>
        <button
          onClick={() => {
            userHasInteracted.current = true;
            setFilters(prev => ({ ...prev, forRent: false }));
            const venuesSection = document.querySelector('[data-venues-section]');
            if (venuesSection) {
              venuesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="inline-flex items-center px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors shadow-sm whitespace-nowrap"
        >
          {t('venues.browseVenues')}
        </button>
        <button
          onClick={() => {
            userHasInteracted.current = true;
            setFilters(prev => ({ ...prev, forRent: true }));
            const venuesSection = document.querySelector('[data-venues-section]');
            if (venuesSection) {
              venuesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="inline-flex items-center px-6 py-3 ml-2 border-2 border-[#8DA847] text-[#8DA847] bg-transparent rounded-full font-medium hover:bg-[#8DA847] hover:text-white transition-colors whitespace-nowrap"
        >
          {t('venues.rentASpace')}
        </button>
        <div className="mt-3">
          <button
            onClick={handleCreateListing}
            className="text-sm text-accent-text hover:text-accent-text/80 underline"
          >
            {t('venues.ownASpace')}
          </button>
        </div>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block relative mb-12 bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white overflow-hidden">
        <div className="py-8 lg:py-16 relative z-10">
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-4 lg:mb-6">{t('venues.title')}</h1>
                <p className="text-lg lg:text-xl text-content/70 mb-6 lg:mb-8">{t('venues.subtitle')}</p>
                <div className="mb-3 flex flex-wrap justify-center lg:justify-start gap-2">
                  <button
                    onClick={() => {
                      userHasInteracted.current = true;
                      setFilters(prev => ({ ...prev, forRent: false }));
                      const venuesSection = document.querySelector('[data-venues-section]');
                      if (venuesSection) {
                        venuesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="inline-flex items-center px-6 lg:px-10 py-3 bg-[#8DA847] text-white rounded-full font-medium hover:bg-[#8DA847]/90 transition-colors whitespace-nowrap"
                  >
                    {t('venues.browseVenues')}
                  </button>
                  <button
                    onClick={() => {
                      userHasInteracted.current = true;
                      setFilters(prev => ({ ...prev, forRent: true }));
                      const venuesSection = document.querySelector('[data-venues-section]');
                      if (venuesSection) {
                        venuesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="inline-flex items-center px-6 lg:px-10 py-3 border-2 border-[#8DA847] text-[#8DA847] bg-transparent rounded-full font-medium hover:bg-[#8DA847] hover:text-white transition-colors whitespace-nowrap"
                  >
                    {t('venues.rentASpace')}
                  </button>
                </div>
                <div>
                  <button
                    onClick={handleCreateListing}
                    className="text-sm text-accent-text hover:text-accent-text/80 underline"
                  >
                    {t('venues.ownASpace')}
                  </button>
                </div>
              </div>
      <div className="hidden lg:block">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-nature-venue.png"
                  alt="Find Your Perfect Wellness Places"
                  className="w-full h-[450px] object-cover blob-shape"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-0 sm:px-6 lg:px-10 mt-16 lg:mt-8" data-venues-section>
        <VenueFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          locationLoading={locationLoading}
          onAddressSelect={handleAddressSelect}
          showExpanded={true}
        />
        <div className="max-w-page mx-auto">

        <div className="px-4 sm:px-0 mt-6">
        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {loading ? (
          <SkeletonGrid count={6} />
        ) : displayVenues.length === 0 ? (
          <div className="bg-background rounded-lg p-8 text-center">
            <p className="text-content">
              {filters.nearMe ? t('venues.noVenuesNearby') : t('venues.noVenuesFilter')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-0">
              {displayVenues.map((venue) => (
                <VenueCard key={venue.id} venue={venue} distance={(venue as any).distance} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setPage(prev => prev + 1)}
                  disabled={loadingMore}
                  className="px-6 py-2.5 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors text-sm disabled:opacity-60"
                >
                  {loadingMore ? 'Loading...' : 'Show more'}
                </button>
              </div>
            )}
          </>
        )}
        </div>
        </div>
      </div>

      {showCreateModal && (
        <VenueForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}
